import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Header } from "datastake-daf/dist/components";
import { useNavigate, useParams } from "react-router-dom";
import Map from "./components/Map";
import Overview from "./components/Overview";
import { useFindInformation } from "../store";
import { useCountry } from "../../../helpers/map";
import QueryService from "../../../services/QueryService";
import { renderBreadCrumbs } from "../../../helpers/breadCrumbs";
import InformationSources from "./components/InformationSources";
import DashboardLayout from "../../../components/dashboards/components/DashboardLayout";
import Graph from "./components/Graph";
import { getTerritory } from "../helper";

export default function FindInformationSubject({ mod }) {
  const goTo = useNavigate();
  const { id } = useParams();
  const { getCountry } = useCountry();
  const allData = useFindInformation();
  const [foundData, setFoundData] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  const type = useMemo(() => {
    if (id.includes('STK')) {
      return 'stakeholder';
    }

    return 'location';
  }, [id]);

  const {
    data: _data,
    loading: _loading,
    form,
    fetchForm,
  } = useMemo(() => type === 'stakeholder' ? allData.entities : allData.locations, [type, allData]);

  const loading = useMemo(() => _loading || loadingData, [_loading, loadingData]);

  const fetchData = useCallback(async () => {
    setLoadingData(true);

    try {
      const { data = [] } = await QueryService.getSubject(id, 'all');
      setFoundData(data);
    } catch (err) {
      console.log(err);
    }

    setLoadingData(false);
  }, [id]);

  useEffect(() => {
    fetchForm();
    fetchData();
  }, []);

  const data = useMemo(() => {
    if (foundData.length > 0) {
      const data = foundData[0] || {};

      if (type === 'stakeholder') {
        const categoryValue = data.category || data.datapoints.find((d) => d.dataId === 'category')?.data;
        const category = (form?.category?.options || []).find((v) => v.value === categoryValue)?.label || categoryValue;
        const subCategoryValue = data.subCategory || data.datapoints.find((d) => d.dataId === 'subCategory')?.data;
        const subCategory = (form?.subCategory?.options || []).find((v) => v.value === subCategoryValue)?.label || subCategoryValue;

        return {
          ...data,
          items: foundData,
          overview: [
            {
              label: 'Name',
              value: data.name,
            },
            {
              label: 'Category',
              value: category || '--',
            },
            {
              label: 'Sub Category',
              value: subCategory || '--',
            },
          ],
        }
      }

      const category = (form?.category?.options || []).find((v) => v.value === data.category)?.label || data.category;
      const territoryValue = data.datapoints.find((d) => d.dataId === 'administrativeLevel2')?.data;
      const gps = data.datapoints.find((d) => d.dataId === 'gps')?.data;

      return {
        ...data,
        gps: { ...gps, name: data.name },
        items: foundData,
        overview: [
          {
            label: 'Name',
            value: data.name,
          },
          {
            label: 'Type',
            value: category || '--',
          },
          {
            label: 'Country',
            value: getCountry(data.country),
          },
          {
            label: getTerritory({ form, country: data.country }),
            value: territoryValue || '--',
          },
        ],
      }
    }

    return {}
  }, [type, _data, id, getCountry, foundData, form]);

  const { t } = useTranslation();

  const breadCrumbs = useMemo(() => {
    const _breadCrumbs = renderBreadCrumbs({ t, view: 'find-information', isDataStore: true, goTo, mod });
    _breadCrumbs.push({ label: type === 'stakeholder' ? t('Entities') : t('Locations'), onClick: () => goTo(`/app/${mod}/find-information/${id}`) });
    return _breadCrumbs;
  }, [t, goTo, mod, id, type]);

  return (
    <DashboardLayout>
      <Header title={t('Find Information')} breadcrumbs={breadCrumbs} />

      <div className="content">
        <div className="view-content">
          <section>
            <Overview type={type} data={data.overview} loading={loading} />

            {type === 'location' && (
              <Map loading={loading} data={data.gps} />
            )}

            {type === 'stakeholder' && (
              <Graph loading={loading} />
            )}
          </section>

          <section>
            <InformationSources fetchData={fetchData} loading={loading} mod={mod} items={data.items} />
          </section>
        </div>
      </div>

    </DashboardLayout>
  )
}
